import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router";

import ChartDot from "../molecules/ChartDot";
import { InfoIconButton } from "../molecules/InfoIconButton";
import Line, { type Position } from "../molecules/Line";
import { type DocumentSummary } from "~/api/types";
import { useTranslation } from "react-i18next";
import DocumentIcon from "../molecules/DocumentIcon";
import { FieldStatus } from "../molecules/FieldStatus";
import { formatDate, isPastDate } from "~/helpers/date";
import cn from "classnames";
import { GripVertical } from "lucide-react";
import { Button } from "~/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";

interface ChartReorderContextValue {
  reorderKey: number;
  notifyReorderStart: () => void;
}

export const ChartReorderContext = createContext<ChartReorderContextValue>({
  reorderKey: 0,
  notifyReorderStart: () => undefined,
});

interface ChartCrossCategoryContextValue {
  documents: DocumentSummary[];
  showCrossCategoryLinksTooltip: boolean;
}

export const ChartCrossCategoryContext =
  createContext<ChartCrossCategoryContextValue>({
    documents: [],
    showCrossCategoryLinksTooltip: false,
  });

const CrossCategoryTooltip = ({
  documents,
}: {
  documents: DocumentSummary[];
}) => {
  const { t } = useTranslation();
  if (documents.length === 0) return null;

  const groups = new Map<number, { title: string; docs: DocumentSummary[] }>();
  for (const document of documents) {
    const { id, title } = document.category;
    const group = groups.get(id) ?? { title, docs: [] };
    group.docs.push(document);
    groups.set(id, group);
  }

  return (
    <TooltipContent className="flex flex-col gap-2 p-2">
      <div className="text-xs font-medium">
        {t(($) => $.chart.linkedDocumentsOutsideCategory)}
      </div>
      <div className="flex flex-col gap-2">
        {Array.from(groups.values()).map((group) => (
          <div key={group.title} className="flex flex-col gap-1">
            <div className="text-muted-foreground text-xs font-semibold">
              {group.title}
            </div>
            <ul className="list-disc pl-4">
              {group.docs.map((document) => (
                <li key={document.id}>
                  <Button variant="link" asChild className="h-auto p-0 text-xs">
                    <Link
                      to={`/documents/${document.id}?versionId=${document.latestPublishedVersion?.id ?? document.latestVersion.id}`}
                    >
                      {document.latestVersion.title}
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </TooltipContent>
  );
};

interface Props {
  document: DocumentSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  isDragging?: boolean;
  handleRef?: (element: Element | null) => void;
}

interface PositionWithId extends Position {
  toId: number;
}

interface DocumentLinesProps {
  document: DocumentSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  documents: DocumentSummary[];
}

export const DocumentLines = ({
  document,
  documentRefsMap,
  containerRef,
  hoveredId,
  documents,
}: DocumentLinesProps) => {
  const { reorderKey } = useContext(ChartReorderContext);
  const [positions, setPositions] = useState<PositionWithId[]>([]);
  const { parentDocumentIds } = document;

  useEffect(() => {
    const measurePositions = () => {
      const newPositions: PositionWithId[] = [];
      const refsMap = documentRefsMap.current;

      parentDocumentIds.forEach((parentId) => {
        const childRef = refsMap?.get(document.id);
        const parentRef = refsMap?.get(parentId);

        if (childRef && parentRef && containerRef?.current) {
          const parent = parentRef.getBoundingClientRect();
          const child = childRef.getBoundingClientRect();
          const container = containerRef.current.getBoundingClientRect();

          newPositions.push({
            parentX: parent.left - container.left,
            parentY: parent.bottom - container.top,
            childX: child.left - container.left,
            childY: child.top - container.top,
            toId: parentId,
          });
        }
      });

      setPositions(newPositions);
    };

    // After a reorder, dnd-kit's drop animation takes ~250ms while the
    // dragged card remains in the browser's popover top layer. The
    // Feedback plugin cleanup (which restores the card to normal document
    // flow) only runs after the animation finishes. Delay measurement so
    // we read final flow positions instead of popover-layer positions.
    if (reorderKey) {
      const timer = setTimeout(measurePositions, 300);
      return () => clearTimeout(timer);
    }

    measurePositions();
  }, [
    containerRef,
    parentDocumentIds,
    document.id,
    documentRefsMap,
    documents,
    reorderKey,
  ]);

  const isHovering = hoveredId === document.id;

  return (
    <>
      {positions.map(({ toId, ...position }) => (
        <Line
          key={toId}
          {...position}
          offsetX={75}
          active={isHovering || hoveredId === toId}
        />
      ))}
    </>
  );
};

export const DocumentCard = ({
  document,
  documentRefsMap,
  hoveredId,
  setHoveredId,
  setSelectedId,
  isDragging,
  handleRef,
}: Props) => {
  const { t } = useTranslation();

  const { parentDocumentIds, childDocumentIds } = document;
  const version = document.latestPublishedVersion ?? document.latestVersion;

  const isHovering = hoveredId === document.id;
  const isHoveringParent = parentDocumentIds.some((id) => hoveredId === id);
  const isHoveringChild = childDocumentIds.some((id) => hoveredId === id);

  const { documents, showCrossCategoryLinksTooltip } = useContext(
    ChartCrossCategoryContext,
  );
  const myCategoryId = document.category.id;
  const fullDocument = showCrossCategoryLinksTooltip
    ? documents.find((doc) => doc.id === document.id)
    : undefined;

  const fullParentIds = fullDocument?.parentDocumentIds ?? [];
  const fullChildIds = fullDocument?.childDocumentIds ?? [];

  const crossCategoryParents = showCrossCategoryLinksTooltip
    ? documents.filter(
        (document) =>
          fullParentIds.includes(document.id) &&
          document.category.id !== myCategoryId,
      )
    : [];

  const crossCategoryChildren = showCrossCategoryLinksTooltip
    ? documents.filter(
        (document) =>
          fullChildIds.includes(document.id) &&
          document.category.id !== myCategoryId,
      )
    : [];

  const isDraft = document.latestPublishedVersion === null;

  const dateAttribute = isPastDate(version.adoptedAt)
    ? "adoptedAt"
    : "publishedAt";

  const formattedDate = formatDate(version[dateAttribute]);

  const dateLabel =
    dateAttribute === "adoptedAt"
      ? t(($) => $.commons.document.adopted)
      : t(($) => $.commons.documentStatus.published);

  return (
    <TooltipProvider>
      <div
        ref={(node) => {
          if (node) {
            documentRefsMap.current?.set(document.id, node);
          } else {
            documentRefsMap.current?.delete(document.id);
          }
        }}
        onMouseEnter={() => setHoveredId(document.id)}
        onMouseLeave={() => setHoveredId(undefined)}
        className={cn(
          "relative size-37.5",
          isDragging &&
            "border-success/50 scale-[0.97] rounded-sm border-2 border-dashed opacity-50",
        )}
      >
        {(parentDocumentIds.length > 0 || crossCategoryParents.length > 0) && (
          <div className="absolute -top-1.5 left-1/2 z-10 -translate-x-1/2">
            <Tooltip>
              <TooltipTrigger asChild>
                <ChartDot
                  hasDot={parentDocumentIds.length > 0}
                  active={isHovering || isHoveringParent}
                  count={crossCategoryParents.length}
                />
              </TooltipTrigger>
              <CrossCategoryTooltip documents={crossCategoryParents} />
            </Tooltip>
          </div>
        )}

        {handleRef && (
          <span
            ref={handleRef}
            className="text-muted-foreground/60 hover:text-muted-foreground absolute top-2 left-2 z-10 cursor-grab"
            aria-hidden="true"
          >
            <GripVertical className="size-4" />
          </span>
        )}

        <div className="absolute top-2 right-2 z-10">
          <InfoIconButton onClick={() => setSelectedId(document.id)} />
        </div>
        <Link
          to={`/documents/${document.id}?versionId=${version.id}&tab=metadata`}
          className={cn(
            "bg-card hover:border-chart-1 relative flex h-full cursor-pointer flex-col overflow-hidden rounded-sm border-2 border-transparent p-2 shadow-md",
            isDragging && "pointer-events-none",
            isDraft && "border-warning/50 hover:border-solid",
          )}
        >
          {isDraft && (
            <>
              <div
                className="bg-warning/50 pointer-events-none absolute right-0 bottom-0 size-15"
                style={{ clipPath: "polygon(100% 35%, 100% 100%, 0 100%)" }}
              />
            </>
          )}
          <div className="size-12 p-2.5">
            <DocumentIcon type={document.latestVersion.fileType} />
          </div>
          <div className="text-secondary-foreground line-clamp-4 flex-1 overflow-hidden text-xs font-medium wrap-break-word hyphens-auto">
            {document.latestVersion.title}
          </div>
          {!isDraft && (
            <div className="text-muted-foreground text-[11px]">
              {`${dateLabel} ${formattedDate}`}
            </div>
          )}
          {isDraft && <FieldStatus status={document.latestVersion.status} />}
        </Link>
        {(childDocumentIds.length > 0 || crossCategoryChildren.length > 0) && (
          <div className="absolute -bottom-3 left-1/2 z-10 -translate-x-1/2">
            <Tooltip>
              <TooltipTrigger asChild>
                <ChartDot
                  hasDot={childDocumentIds.length > 0}
                  active={isHovering || isHoveringChild}
                  count={crossCategoryChildren.length}
                />
              </TooltipTrigger>
              <CrossCategoryTooltip documents={crossCategoryChildren} />
            </Tooltip>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};
